import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";
import { fmtCurrency } from "../../lib/format";

const COLS = [
  { key: "location", label: "Location", align: "left" },
  { key: "revenue", label: "Revenue", fmt: fmtCurrency },
  { key: "net_income", label: "Net income", fmt: fmtCurrency, neg: true },
  { key: "occupancy_pct", label: "Occupancy", fmt: (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`) },
  { key: "members", label: "Members" },
  { key: "member_mrr", label: "Member MRR", fmt: fmtCurrency },
  { key: "vacant_offices", label: "Vacant" },
  { key: "notice_mrr", label: "On notice", fmt: (v) => (v ? fmtCurrency(v) : "—"), warn: true },
  { key: "delinquent", label: "Delinq.", fmt: (v) => v || "—", warn: true },
  { key: "pipeline", label: "Pipeline", fmt: (v) => (v ? fmtCurrency(v) : "—") },
  { key: "today", label: "Today", fmt: (_, r) => `${r.rooms_today} rooms · ${r.day_passes_today} passes` },
];

const sum = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);

export function ExecLocationTable({ rows }) {
  const nav = useNavigate();
  const [sort, setSort] = useState({ key: "revenue", dir: -1 });
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const x = a[sort.key] ?? -Infinity, y = b[sort.key] ?? -Infinity;
    return (typeof x === "string" ? x.localeCompare(y) : x - y) * sort.dir;
  }), [rows, sort]);
  const tot = { location: "Portfolio", revenue: sum(rows, "revenue"), net_income: sum(rows, "net_income"), members: sum(rows, "members"),
    member_mrr: sum(rows, "member_mrr"), vacant_offices: sum(rows, "vacant_offices"), notice_mrr: sum(rows, "notice_mrr"),
    delinquent: sum(rows, "delinquent"), pipeline: sum(rows, "pipeline"), rooms_today: sum(rows, "rooms_today"), day_passes_today: sum(rows, "day_passes_today") };

  const cell = (c, r) => {
    const v = r[c.key];
    const txt = c.fmt ? c.fmt(v, r) : (v ?? "—");
    const tone = (c.neg && v < 0) || (c.warn && v) ? "text-rose-600" : "text-brand-ink";
    return <td key={c.key} className={`px-4 py-3 whitespace-nowrap text-sm ${c.align === "left" ? "text-left font-medium" : "text-right tabular-nums"} ${tone}`}>{txt}</td>;
  };

  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden" data-testid="exec-location-table">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="border-b border-brand-border">
              {COLS.map((c) => (
                <th key={c.key} className={`px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-normal text-brand-tertiary ${c.align === "left" ? "text-left" : "text-right"}`}>
                  {c.key === "today" ? c.label : (
                    <button onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))} data-testid={`exec-sort-${c.key}`}
                      className={`inline-flex items-center gap-1 hover:text-brand-ink transition-colors ${sort.key === c.key ? "text-brand-ink" : ""}`}>
                      {c.label}<ArrowUpDown className="h-3 w-3" />
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.location} onClick={() => nav(`/locations/${encodeURIComponent(r.location)}`)} data-testid={`exec-loc-row-${r.location.replace(/\s+/g, "-").toLowerCase()}`}
                className="border-b border-brand-border last:border-0 cursor-pointer hover:bg-brand-hover transition-colors">
                {COLS.map((c) => cell(c, r))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-brand-ink/[0.03] border-t border-brand-borderStrong" data-testid="exec-loc-total">
              {COLS.map((c) => (c.key === "occupancy_pct" ? <td key={c.key} className="px-4 py-3 text-right text-sm text-brand-muted">—</td> : cell(c, tot)))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
